import { isAcceptedMediaType, MAX_ATTACHMENTS_PER_INSPECTION, uploadLimitOf } from '@ghp/domain'
import {
  Button,
  Card,
  Field,
  FileInput,
  IconButton,
  InfoNote,
  Input,
  ProgressBar,
  SegmentedControl,
  Select,
  Tag,
} from '@ghp/ui'
import { Camera, FilePlus2, FileText, Info, Video, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import type { LocalMedium } from '../../../offline/data-db'
import { useUploadProgress } from '../../../offline/direct-upload'
import { rememberedVideoLimit } from '../../../offline/media-policy'
import { stampLabel } from '../labels'
import { AddFileModal, type AttachmentKind } from './AddFileModal'
import s from './AttachmentsTab.module.css'

/**
 * Onglet « Pièces jointes » — ajout, horodatage, famille, légende.
 *
 * La capture passe par un `<input type="file">` : c'est l'appareil du navigateur, pas un module
 * natif — **Capacitor reste écarté** (D13), la PWA sait le faire.
 *
 * Ni compression ni miniature en S4 : l'aperçu est le fichier lui-même via
 * `URL.createObjectURL`. ⚠️ C'est un point de vigilance réel, pas un détail — quelques
 * dizaines de photos de 4 Mo affichées en pleine résolution sur une tablette d'entrée de
 * gamme, ce n'est pas la même chose qu'un prototype à données figées. Les vidéos ne sont, elles,
 * **pas** décodées pour la vignette : une tournée en porterait plusieurs centaines de Mo.
 */
export interface AttachmentsTabProps {
  media: LocalMedium[]
  /** Fiche transmise : les pièces se consultent, on n'en ajoute plus (D4). */
  disabled?: boolean
  /**
   * Une pièce vient d'être choisie, avec la légende que l'agent a écrite dans la fenêtre
   * d'ajout — `null` s'il n'en a pas écrit.
   *
   * ⚠️ La légende est **reprise au back-office** : c'est elle qui dit ce que la photo montre,
   * et personne ne la retape une fois la tournée finie. Un conteneur qui l'ignorerait rangerait
   * la pièce sans ce que l'agent a pris le temps d'écrire.
   */
  onCapture?: (file: File, caption: string | null) => void
  onTypeChange?: (mediumId: string, type: AttachmentType) => void
  /**
   * Le nom de la pièce, repris sur sa vignette — `null` quand l'agent le vide. Remonté à chaque
   * frappe : une photo prise directement n'a pas de nom, c'est ici qu'elle le reçoit.
   */
  onLabelChange?: (mediumId: string, label: string | null) => void
  /**
   * L'agent a confirmé le retrait d'une pièce — une photo floue, prise par erreur (#320).
   *
   * ⚠️ Retirer avant la transmission suffit à ce que la pièce ne parte nulle part : l'enveloppe
   * se compose au verrou depuis les pièces encore rangées, et les octets ne montent qu'après
   * (`enqueueSubmission`, `sendEntry`). Sans rappel, la croix se présente désactivée.
   */
  onRemove?: (mediumId: string) => void
  /**
   * Ce qu'une vidéo peut peser : 1 Go quand le serveur la prend en direct, 25 Mo par le relais
   * (#418). Lu de la dernière synchro ; 25 Mo tant que la tablette ne le sait pas.
   */
  videoLimitBytes?: number
}

/**
 * Les médias que la tablette **produit** au lieu de les capturer : le paraphe et l'image rendue
 * du schéma réseau (M11). Ni l'un ni l'autre n'est une pièce jointe — les ranger ici les
 * rendrait reclassables comme une photo, et les ferait compter dans un badge que l'agent lirait
 * comme le nombre de clichés qu'il a pris.
 */
const GENERATED_TYPES = ['signature', 'schematic'] as const

/**
 * La ligne de partage « pièce jointe / média produit », **écrite une seule fois**. Le badge de
 * l'onglet et la liste des pièces s'en écartaient chacun de son côté — 1 ici, 2 là, sur le même
 * contrôle (#181).
 */
export function isAttachment(medium: LocalMedium): boolean {
  return !GENERATED_TYPES.some((type) => type === medium.type)
}

/** Les quatre **familles** qu'une pièce peut prendre : ce qu'elle montre. */
export type AttachmentType = Exclude<LocalMedium['type'], (typeof GENERATED_TYPES)[number]>

/**
 * « Schéma » en tête, comme la barre de filtres du prototype : c'est la photo d'un schéma fait sur
 * papier, que le rapport imprime dans sa section « Schéma de l'installation » (#397).
 */
const TYPE_OPTIONS: { value: AttachmentType; label: string }[] = [
  { value: 'schematic_photo', label: 'Schéma' },
  { value: 'view', label: 'Vue' },
  { value: 'manhole', label: 'Regard' },
  { value: 'stormwater', label: 'Eaux pluviales' },
]

/**
 * Ce que l'agent ouvre pour chaque type de fichier. La photo et la vidéo passent par l'objectif
 * **arrière** : sans `capture`, une tablette ouvre celui de visioconférence, celui qui regarde
 * l'agent. Un document, lui, se prend sur la tablette — il n'y a rien à filmer.
 */
const fullHint = `${String(MAX_ATTACHMENTS_PER_INSPECTION)} pièces au plus`

const PICKERS: Record<AttachmentKind, { label: string; accept: string; capture?: 'environment' }> =
  {
    photo: { label: 'Ajouter une photo', accept: 'image/*', capture: 'environment' },
    document: { label: 'Ajouter un document', accept: 'application/pdf,image/*' },
    video: { label: 'Ajouter une vidéo', accept: 'video/*', capture: 'environment' },
  }

/**
 * De quoi est faite une pièce déjà rangée — **lu sur le fichier lui-même**, jamais stocké.
 *
 * ⚠️ C'est ce qui évite une migration Dexie pour une information que le `Blob` porte déjà : le
 * type MIME est écrit par l'appareil au moment de la prise, et il survit au redémarrage comme le
 * reste de la pièce. Un champ de plus serait une seconde vérité, à tenir d'accord avec celle-là.
 *
 * ⚠️ Un type MIME absent vaut **photo** : c'est ce qu'ont rangé toutes les captures d'avant la
 * fenêtre d'ajout, et une pièce classée « document » afficherait une icône à la place de la
 * photo que l'agent a prise.
 */
function kindOf(medium: LocalMedium): AttachmentKind {
  /*
   * ⚠️ **Le fichier lui-même peut manquer**, et lire son type sans le vérifier faisait tomber
   * l'onglet entier — donc l'éditeur, derrière sa frontière d'erreur : l'agent perdait l'écran
   * au lieu de perdre une vignette. Une pièce dont le `Blob` n'est pas relu vaut photo, comme
   * une pièce sans type MIME : on affiche ce qu'on a et on laisse l'humain trancher (M21).
   */
  const mimeType = medium.blob?.type ?? ''

  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType === '' || mimeType.startsWith('image/')) return 'photo'

  return 'document'
}

/**
 * Les familles de la barre de filtres, « Toutes » et « Vidéos » comprises.
 *
 * ⚠️ **Deux axes, pas un seul** : « Schéma », « Vue », « Regard » et « Eaux pluviales » disent ce
 * que la pièce montre, « Vidéos » dit de quoi elle est faite. Une vidéo de regard répond donc aux
 * deux, et c'est aussi le cas dans le prototype, dont les familles se recouvrent. « Vue »,
 * « Regard » et « Eaux pluviales » viennent du test terrain du 25/08 ; « Vidéos » s'y ajoute, sans
 * quoi le type de fichier que l'app vient d'accepter n'a aucun filtre.
 *
 * ⚠️ « Schéma » ne compte que les photos que l'agent a marquées ainsi (#397) — jamais l'image
 * d'archive du dessin, que {@link isAttachment} écarte : c'est un média **produit** (M11).
 *
 * ⚠️ **« Non conformes » manque, faute de donnée** (arbitrage du 21/09) : rien ne marque une
 * pièce non conforme. Les photos du prototype portent un drapeau `nc` que ses données posent
 * d'avance ; ni `LocalMedium` ni aucun écran ne donne à l'agent le moyen de le poser. Fabriquer la
 * famille reviendrait à inventer la saisie qui la remplit, ce qui est pire que son absence.
 */
type AttachmentFilter = 'all' | AttachmentType | 'video'

/**
 * Pourquoi l'API refuserait ce fichier, ou `null` s'il passe — **sa** politique, lue dans le
 * domaine. ⚠️ Un refus à l'envoi (`413`, `415`) gare le relevé entier jusqu'à ce que l'agent rouvre
 * la fiche pour retirer la pièce. Elle est donc écartée au choix, et l'agent le lit tout de suite.
 */
function refusalOf(file: File, videoLimitBytes: number): string | null {
  if (!isAcceptedMediaType(file.type)) {
    return `« ${file.name} » est dans un format que le serveur n’accepte pas (${file.type || 'format inconnu'}). La pièce n’est pas jointe, et le relevé reste transmissible.`
  }
  const limit = uploadLimitOf(file.type, videoLimitBytes)
  if (file.size > limit) {
    return `« ${file.name} » pèse ${String(Math.ceil(file.size / 1024 / 1024))} Mo : le serveur en accepte ${String(limit / 1024 / 1024)} au plus. La pièce n’est pas jointe, et le relevé reste transmissible.`
  }

  return null
}

export function AttachmentsTab({
  media,
  disabled,
  onCapture,
  onTypeChange,
  onLabelChange,
  onRemove,
  videoLimitBytes = rememberedVideoLimit(),
}: AttachmentsTabProps) {
  const [filter, setFilter] = useState<AttachmentFilter>('all')
  const [adding, setAdding] = useState(false)
  const pickers = {
    photo: useRef<HTMLInputElement>(null),
    document: useRef<HTMLInputElement>(null),
    video: useRef<HTMLInputElement>(null),
  }
  /**
   * La légende choisie dans la fenêtre, le temps que l'agent prenne la photo.
   *
   * Une ref et non un état : ce qui la relit est le `change` de l'`<input>`, une éternité plus
   * tard à l'échelle d'un rendu — et rien à l'écran n'en dépend entre-temps.
   */
  const caption = useRef<string | null>(null)
  /** Le fichier refusé au choix parce que le serveur ne le prendrait pas — dit, jamais avalé. */
  const [refusal, setRefusal] = useState<string | null>(null)

  const photos = media.filter(isAttachment)
  /** GHP, 22/09 : dix pièces au plus — un blocage, exception nommée à M21. */
  const full = photos.length >= MAX_ATTACHMENTS_PER_INSPECTION
  const shown =
    filter === 'all'
      ? photos
      : photos.filter((medium) =>
          filter === 'video' ? kindOf(medium) === 'video' : medium.type === filter,
        )

  /**
   * Les compteurs sont **calculés**, jamais écrits. Ceux du prototype sont en dur et faux
   * — « Non conformes 4 » pour deux photos — et un compteur qui mentait sur une tournée
   * réelle enverrait l'agent chercher une photo qu'il n'a pas prise.
   *
   * Les familles vides restent affichées : leur zéro dit à l'agent ce qu'il lui reste à
   * photographier, là où une barre qui se réduirait au fil des prises ne dirait rien.
   */
  const filters: readonly { value: AttachmentFilter; label: string; count: number }[] = [
    { value: 'all', label: 'Toutes', count: photos.length },
    ...TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
      count: photos.filter((medium) => medium.type === option.value).length,
    })),
    {
      value: 'video',
      label: 'Vidéos',
      count: photos.filter((medium) => kindOf(medium) === 'video').length,
    },
  ]

  return (
    <div className={s.sections}>
      {photos.length > 0 && (
        <SegmentedControl
          label="Filtrer les pièces par famille"
          options={filters}
          value={filter}
          onValueChange={setFilter}
        />
      )}

      {full && (
        <InfoNote tone="warn" icon={<Info size={15} />} role="status">
          <b>
            {MAX_ATTACHMENTS_PER_INSPECTION} pièces sur {MAX_ATTACHMENTS_PER_INSPECTION}
          </b>{' '}
          : c’est le maximum d’un contrôle. Retirez une pièce pour en ajouter une autre.
        </InfoNote>
      )}

      <ul className={s.grid}>
        {/*
         * Les tuiles d'ajout sont **dans la grille**, à ses premières places : c'est là que l'agent
         * regarde après avoir compté ce qu'il a déjà pris, et une carte posée au-dessus l'en
         * éloignait d'un écran de défilement sur une fiche à vingt photos.
         */}
        <li>
          {/*
           * GHP, 22/09 : « Photo » ouvre directement l'appareil — sans fenêtre, le nom se donne
           * ensuite sur la vignette. Le `click()` reste dans le fil de l'appui : c'est ce qui
           * autorise le navigateur à ouvrir l'appareil.
           */}
          <button
            type="button"
            className={s.add}
            disabled={disabled === true || full}
            onClick={() => {
              caption.current = null
              pickers.photo.current?.click()
            }}
          >
            <Camera size={30} aria-hidden />
            <span className={s.addLabel}>Prendre une photo</span>
            <span className={s.addHint}>{full ? fullHint : 'ouvre l’appareil photo'}</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className={s.add}
            disabled={disabled === true || full}
            onClick={() => {
              setAdding(true)
            }}
          >
            <FilePlus2 size={30} aria-hidden />
            <span className={s.addLabel}>Ajouter un fichier</span>
            <span className={s.addHint}>{full ? fullHint : 'document · vidéo'}</span>
          </button>
        </li>

        {shown.map((medium) => (
          <Attachment
            key={medium.id}
            medium={medium}
            disabled={disabled}
            onTypeChange={onTypeChange}
            onLabelChange={onLabelChange}
            onRemove={onRemove}
          />
        ))}
      </ul>

      {photos.length === 0 && (
        <p className={s.noMatch} role="status">
          Aucune pièce jointe. Les fichiers ajoutés ici restent sur la tablette jusqu’à la
          transmission.
        </p>
      )}
      {photos.length > 0 && shown.length === 0 && (
        <p className={s.noMatch} role="status">
          Aucune pièce de cette famille sur ce contrôle.
        </p>
      )}

      <InfoNote icon={<Info size={15} />}>
        Jusqu’à <b>{MAX_ATTACHMENTS_PER_INSPECTION} pièces</b> par contrôle — {photos.length}{' '}
        {photos.length > 1 ? 'jointes' : 'jointe'}. Chaque pièce porte un <b>nom</b>, repris dans le
        back-office.
      </InfoNote>
      {refusal !== null && (
        <InfoNote tone="warn" icon={<Info size={15} />} role="alert">
          {refusal}
        </InfoNote>
      )}

      {/*
       * Un sélecteur par type de fichier, plutôt qu'un seul dont on changerait les attributs :
       * ouvrir l'appareil exige un geste de l'utilisateur, donc un `click()` dans le fil du sien
       * — le temps qu'un rendu React repose `accept`, le navigateur a perdu cette autorisation.
       *
       * ⚠️ `FileInput`, pas `FileDropzone` : le type et le nom d'un document ou d'une vidéo se
       * choisissent **avant** la prise, dans la fenêtre d'ajout.
       */}
      {Object.entries(PICKERS).map(([kind, picker]) => (
        <FileInput
          key={kind}
          ref={pickers[kind as AttachmentKind]}
          className={s.file}
          aria-label={picker.label}
          accept={picker.accept}
          capture={picker.capture}
          disabled={disabled === true || full}
          onFile={(file) => {
            const refused = refusalOf(file, videoLimitBytes)
            setRefusal(refused)
            if (refused === null) onCapture?.(file, caption.current)
            caption.current = null
          }}
        />
      ))}

      <AddFileModal
        open={adding}
        videoLimitBytes={videoLimitBytes}
        onOpenChange={setAdding}
        onConfirm={(kind, written) => {
          caption.current = written
          setAdding(false)
          pickers[kind].current?.click()
        }}
      />
    </div>
  )
}

/** Le mot que porte une pièce qui n'est pas une photo, comme le prototype l'étiquette. */
const KIND_LABEL: Record<Exclude<AttachmentKind, 'photo'>, string> = {
  document: 'Document',
  video: 'Vidéo',
}

function Attachment({
  medium,
  disabled,
  onTypeChange,
  onLabelChange,
  onRemove,
}: {
  medium: LocalMedium
  disabled?: boolean
  onTypeChange?: (mediumId: string, type: AttachmentType) => void
  onLabelChange?: (mediumId: string, label: string | null) => void
  onRemove?: (mediumId: string) => void
}) {
  /**
   * Le retrait se confirme **dans la vignette**, pas dans une fenêtre : l'agent voit la photo
   * qu'il s'apprête à retirer au moment où il décide. Une donnée de terrain ne se reconstitue
   * pas — un appui de trop sur une croix de 44 px ne doit pas suffire à la perdre.
   */
  const [confirming, setConfirming] = useState(false)
  const kind = kindOf(medium)
  // `?? null` pour la même raison que dans `kindOf` : sans fichier relu, la vignette reste
  // vide et la pièce s'affiche quand même, avec sa légende et son horodatage.
  const preview = usePreview(kind === 'photo' ? (medium.blob ?? null) : null)
  const labelId = useId()
  /*
   * Le champ tient sa propre valeur : relire `medium.label` à chaque frappe ferait sauter le
   * curseur dès que la base locale répond plus lentement que le doigt.
   */
  const [name, setName] = useState(medium.label ?? '')
  /** L'envoi direct en cours d'une vidéo (#418), entre 0 et 1 ; `null` hors envoi. */
  const sending = useUploadProgress(medium.id)

  return (
    <li>
      <Card className={s.tileCard}>
        {kind === 'photo' ? (
          preview === null ? (
            <span className={s.noPreview}>Aperçu indisponible</span>
          ) : (
            <img className={s.preview} src={preview} alt="" />
          )
        ) : (
          // Ni lecteur ni rendu de page : la vignette nomme ce qu'il y a dans la pièce, comme
          // le prototype. Décoder une vidéo de tournée pour une image de 15 rem coûterait à
          // l'agent la fluidité de tout l'onglet.
          <span className={s.noPreview}>
            {kind === 'video' ? (
              <Video size={30} aria-hidden />
            ) : (
              <FileText size={30} aria-hidden />
            )}
            {KIND_LABEL[kind]}
          </span>
        )}
        {sending !== null && (
          <span className={s.sending}>
            <ProgressBar value={sending} label="Envoi de la vidéo" />
            Envoi {Math.round(sending * 100)} %
          </span>
        )}
        <span className={s.meta}>
          {kind !== 'photo' && (
            <Tag tone={kind === 'video' ? 'blue' : 'neutral'}>{KIND_LABEL[kind]}</Tag>
          )}
          {/* Reprise de la visite précédente par une contre-visite (#675) : l'agent sait ce qui
              était déjà là, et ce qu'il a pris lui-même. */}
          {medium.carriedFrom != null && <Tag tone="neutral">Visite précédente</Tag>}
          <span className={s.metaEnd}>
            <span className={s.taken}>{stampLabel(medium.takenAt) ?? 'Sans horodatage'}</span>
            {!confirming && (
              <IconButton
                label="Retirer la pièce"
                disabled={disabled || onRemove === undefined}
                onClick={() => {
                  setConfirming(true)
                }}
              >
                <X size={15} aria-hidden />
              </IconButton>
            )}
          </span>
        </span>
        {confirming && (
          <div className={s.confirm} role="group" aria-label="Retirer cette pièce ?">
            <span>Retirer cette pièce ? Elle ne partira pas avec le contrôle.</span>
            <span className={s.confirmActions}>
              <Button
                size="sm"
                // Le focus va sur le geste qui ne perd rien : un second appui au même endroit
                // — le doigt n'a pas bougé — ne doit pas valoir confirmation.
                autoFocus
                onClick={() => {
                  setConfirming(false)
                }}
              >
                Annuler
              </Button>
              <Button
                size="sm"
                tone="danger"
                disabled={disabled}
                onClick={() => {
                  setConfirming(false)
                  onRemove?.(medium.id)
                }}
              >
                <X size={16} aria-hidden />
                Retirer
              </Button>
            </span>
          </div>
        )}
        {/* Vide, le champ le dit sans rien refuser (M21) : la pièce reste jointe. */}
        <Field
          label="Nom de la pièce"
          warning={
            name.trim() === '' ? 'Sans nom : le bureau ne saura pas ce qu’elle montre.' : undefined
          }
        >
          {({ id, hintId }) => (
            <Input
              id={id}
              aria-describedby={hintId}
              value={name}
              disabled={disabled === true || onLabelChange === undefined}
              placeholder="ex. Regard EU côté rue"
              onValueChange={(next) => {
                setName(next)
                onLabelChange?.(medium.id, next.trim() === '' ? null : next.trim())
              }}
            />
          )}
        </Field>
        <span className={s.typeLabel} id={labelId}>
          Type de pièce
        </span>
        <Select
          aria-labelledby={labelId}
          options={TYPE_OPTIONS}
          // Un média **produit** n'a pas de famille à choisir — et il n'arrive jamais ici, la
          // liste l'ayant déjà écarté (`isAttachment`). Le repli à `null` est ce qui garde les
          // deux règles alignées quand une troisième famille produite apparaîtra.
          value={isAttachment(medium) ? (medium.type as AttachmentType) : null}
          disabled={disabled}
          onValueChange={(type) => {
            if (type !== null) onTypeChange?.(medium.id, type)
          }}
        />
      </Card>
    </li>
  )
}

/**
 * L'URL d'aperçu d'un `Blob`, **révoquée au démontage** : sans cela, chaque photo garde son
 * fichier vivant en mémoire pour toute la durée de la session — et une tournée, c'est des
 * dizaines de photos.
 *
 * ⚠️ L'aperçu est un **confort**, la photo est la donnée. Un environnement qui refuse
 * `createObjectURL` (jsdom, navigateur restreint, quota épuisé) ne doit pas faire disparaître
 * la liste des pièces : on rend `null` et la vignette le dit.
 */
function usePreview(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (blob === null) return

    let created: string
    try {
      created = URL.createObjectURL(blob)
    } catch {
      return
    }

    setUrl(created)

    return () => {
      URL.revokeObjectURL(created)
    }
  }, [blob])

  return url
}
